import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../theme';
import { FaPhone, FaHospital, FaTrain, FaBus, FaTaxi, FaInfoCircle, FaLanguage, FaSuitcase, FaMapMarkedAlt, FaUmbrellaBeach, FaUtensils, FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { MdLocalHospital, MdLocalPolice, MdDirectionsCar, MdLanguage, MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';

const Banner = styled.div`
  padding: 0.5rem 1rem;
  margin: 0 0 1.5rem 0;
  border-radius: 8px;
  background: linear-gradient(90deg, #E31B23, #FFD700, #006E33);
  color: white;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  h2 {
    margin: 0 0 2px 0;
    font-size: 1.1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    line-height: 1.3;
    
    @media (max-width: 768px) {
      font-size: 1rem;
      flex-wrap: wrap;
      gap: 0.25rem 0.5rem;
    }
  }
  
  .divider {
    opacity: 0.7;
    font-weight: 300;
    @media (max-width: 768px) {
      display: inline;
    }
  }
  
  .promo-text {
    font-size: 0.75rem;
    opacity: 0.9;
    margin-top: 0.2rem;
    display: block;
    font-style: italic;
    line-height: 1.3;
    
    @media (max-width: 480px) {
      font-size: 0.7rem;
    }
  }
  
  .highlight {
    font-weight: 600;
    color: #FFD700;
    text-shadow: 0 0 2px rgba(0,0,0,0.3);
  }
`;

const Panel = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  max-width: 800px;
  margin: 0 auto 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: ${colors.primary};
  margin-bottom: 1rem;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Item = styled.li`
  padding: 0.75rem 0;
  border-bottom: 1px solid ${colors.border};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

const Icon = styled.span`
  font-size: 1.2rem;
  color: ${colors.secondary};
`;

const EmergencyNumber = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  
  strong {
    color: ${colors.primary};
    min-width: 100px;
    display: inline-block;
  }
`;

const TransportLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TabButton = styled.button`
  background: ${({ $active }) => ($active ? colors.primary : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : colors.text)};
  border: 1px solid ${({ $active }) => ($active ? colors.primary : colors.border)};
  padding: 0.6rem 1.2rem;
  border-radius: 24px;
  margin: 0 0.5rem 1rem 0;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    border-color: ${colors.primary};
    transform: translateY(-1px);
  }
  
  svg {
    font-size: 1.1em;
  }
`;

// Default checklist structure
const defaultChecklistStructure = {
  documents: [
    { id: 'passport', text: 'Valid passport (6+ months validity)', checked: false },
    { id: 'visa', text: 'Visa (if required)', checked: false },
    { id: 'ehc', text: 'European Health Card (EHIC) or GHIC', checked: false },
    { id: 'travel_insurance', text: 'Travel insurance documents', checked: false },
    { id: 'itinerary', text: 'Travel itinerary and booking confirmations', checked: false },
    { id: 'accommodation', text: 'Accommodation details and addresses', checked: false },
  ],
  money: [
    { id: 'currency', text: 'Euros (€) in cash', checked: false },
    { id: 'credit_card', text: 'Credit/debit cards (notify bank of travel)', checked: false },
    { id: 'emergency_funds', text: 'Emergency cash in USD or EUR', checked: false },
  ],
  electronics: [
    { id: 'adapter', text: 'Type F power adapter (2 round pins)', checked: false },
    { id: 'chargers', text: 'Chargers for all devices', checked: false },
    { id: 'power_bank', text: 'Portable power bank', checked: false },
    { id: 'earbuds', text: 'Headphones/earbuds', checked: false },
  ],
  clothing: [
    { id: 'comfortable_shoes', text: 'Comfortable walking shoes', checked: false },
    { id: 'light_jacket', text: 'Light jacket (even in summer)', checked: false },
    { id: 'swimwear', text: 'Swimwear', checked: false },
    { id: 'sun_protection', text: 'Sun protection (hat, sunglasses, sunscreen)', checked: false },
    { id: 'umbrella', text: 'Compact umbrella or rain jacket', checked: false },
  ],
  health: [
    { id: 'medication', text: 'Prescription medications (with copies of prescriptions)', checked: false },
    { id: 'first_aid', text: 'Basic first aid kit', checked: false },
    { id: 'hand_sanitizer', text: 'Hand sanitizer', checked: false },
    { id: 'masks', text: 'Face masks (if required)', checked: false },
  ],
  misc: [
    { id: 'guidebook', text: 'Travel guidebook or downloaded guides', checked: false },
    { id: 'phrases', text: 'Portuguese phrasebook or app', checked: false },
    { id: 'reusable_bottle', text: 'Reusable water bottle', checked: false },
    { id: 'day_bag', text: 'Day bag for sightseeing', checked: false },
  ]
};

const EssentialsPanel = ({ checklist = defaultChecklistStructure, onToggleCheckbox }) => {
  const [activeTab, setActiveTab] = useState('checklist');
  
  // Use the provided checklist or fall back to default structure
  const currentChecklist = Object.keys(checklist).length ? checklist : defaultChecklistStructure;

  const emergencyContacts = {
    immediate: [
      { name: 'European Emergency Number', number: '112', icon: <FaPhone />, type: 'emergency' },
      { name: 'Police (Non-emergency)', number: '217 654 242', icon: <MdLocalPolice />, type: 'police' },
    ],
    medical: [
      { name: 'Medical Emergency', number: '112', icon: <FaHospital />, type: 'medical' },
      { name: 'SNS 24 (Health Line)', number: '808 24 24 24', icon: <FaHospital />, type: 'medical' },
    ],
    embassies: [
      { name: 'US Embassy', number: '+351 21 770 2121', icon: '🇺🇸' },
      { name: 'UK Embassy', number: '+351 21 392 4000', icon: '🇬🇧' },
      { name: 'Canadian Embassy', number: '+351 21 316 4600', icon: '🇨🇦' },
    ]
  };

  const transportInfo = {
    publicTransport: [
      { name: 'CP Trains', url: 'https://www.cp.pt/passageiros/en', icon: <FaTrain />, description: 'National train services' },
      { name: 'Metro Lisboa', url: 'https://www.metrolisboa.pt/en/', icon: <FaTrain />, description: 'Lisbon Metro' },
      { name: 'Carris (Buses)', url: 'https://www.carris.pt/en/home/', icon: <FaBus />, description: 'Lisbon buses and trams' },
    ],
    rideHailing: [
      { name: 'Bolt', url: 'https://bolt.eu/', icon: <FaTaxi />, code: 'BOLTAPP' },
      { name: 'Uber', url: 'https://www.uber.com/', icon: <FaTaxi /> },
      { name: 'Free Now', url: 'https://free-now.com/', icon: <FaTaxi /> },
    ],
    tips: [
      '🚍 Buy a Viva Viagem card for public transport in Lisbon',
      '🚖 Ride-hailing apps are generally cheaper than hailing taxis',
      '🚆 Book train tickets in advance for long-distance routes',
      '🚲 Consider using Gira bikes for short trips in Lisbon'
    ]
  };

  const travelEssentials = {
    documents: [
      { text: 'Valid passport (6+ months validity)', icon: <FaSuitcase /> },
      { text: 'Travel insurance (medical coverage)', icon: <FaSuitcase /> },
      { text: 'European Health Insurance Card (EHIC)', icon: <FaSuitcase /> },
      { text: 'Driving license (if renting a car)', icon: <FaSuitcase /> },
    ],
    electronics: [
      { text: 'Type F power adapter (2 round pins)', icon: '🔌' },
      { text: 'Portable charger', icon: '🔋' },
      { text: 'Universal plug adapter', icon: '🔌' },
    ],
    clothing: [
      { text: 'Comfortable walking shoes', icon: '👟' },
      { text: 'Light jacket (even in summer)', icon: '🧥' },
      { text: 'Sun protection (hat, sunscreen)', icon: '🧴' },
      { text: 'Swimwear (for beaches/pools)', icon: '🩱' },
    ]
  };

  const languageInfo = {
    basicPhrases: [
      { pt: 'Bom dia', en: 'Good morning', pronunciation: 'bom DEE-ah' },
      { pt: 'Boa tarde', en: 'Good afternoon', pronunciation: 'BOH-ah TAR-deh' },
      { pt: 'Obrigado (m) / Obrigada (f)', en: 'Thank you', pronunciation: 'oh-bree-GAH-doo/dah' },
      { pt: 'Por favor', en: 'Please', pronunciation: 'por fah-VOHR' },
      { pt: 'Desculpe', en: 'Sorry/Excuse me', pronunciation: 'desh-KOOL-peh' },
    ],
    emergencyPhrases: [
      { pt: 'Preciso de ajuda!', en: 'I need help!', pronunciation: 'preh-SEE-zoo deh ah-ZHOO-dah' },
      { pt: 'Chame a polícia!', en: 'Call the police!', pronunciation: 'SHAH-meh ah poh-LEE-see-ah' },
      { pt: 'Preciso de um médico', en: 'I need a doctor', pronunciation: 'preh-SEE-zoo deh oon MEH-dee-koo' },
      { pt: 'Onde fica o hospital mais próximo?', en: 'Where is the nearest hospital?', pronunciation: 'OHN-deh FEE-kah oo osh-pee-TAHL mah-eesh PROH-see-moo' },
    ],
    diningPhrases: [
      { pt: 'A conta, por favor', en: 'The bill, please', pronunciation: 'ah KOHN-tah por fah-VOHR' },
      { pt: 'Tem menu vegetariano?', en: 'Do you have a vegetarian menu?', pronunciation: 'teng meh-NOO veh-zheh-tah-ree-AH-noo' },
      { pt: 'Água sem gás/com gás', en: 'Still/sparkling water', pronunciation: 'AH-gwah sang GAHSH/kom GAHSH' },
    ]
  };

  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getProgress = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    const total = items.length;
    const completed = items.filter(item => item.checked).length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const totalProgress = () => {
    if (!currentChecklist) return 0;
    const allItems = Object.values(currentChecklist).flat();
    return getProgress(allItems);
  };
  
  const handleCheckboxToggle = (category, id) => {
    if (onToggleCheckbox) {
      onToggleCheckbox(category, id);
    }
  };

  return (
    <>
      <Banner>
        <h2>
          <span>Portugal Travel Essentials</span>
          <span className="divider">•</span>
          <span>Must-Know Info</span>
        </h2>
        <span className="promo-text">
          <span className="highlight">Quick Access:</span> Important contacts and information for your trip
        </span>
      </Banner>
      <Panel>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          borderBottom: `1px solid ${colors.border}`,
          paddingBottom: '0.5rem',
          marginBottom: '1rem'
        }}>
          <TabButton 
            $active={activeTab === 'emergency'} 
            onClick={() => setActiveTab('emergency')}
          >
            <FaHospital /> Emergency
          </TabButton>
          <TabButton 
            $active={activeTab === 'transport'} 
            onClick={() => setActiveTab('transport')}
          >
            <FaTrain /> Transport
          </TabButton>
          <TabButton 
            $active={activeTab === 'essentials'} 
            onClick={() => setActiveTab('essentials')}
          >
            <FaSuitcase /> Essentials
          </TabButton>
          <TabButton 
            $active={activeTab === 'language'} 
            onClick={() => setActiveTab('language')}
          >
            <FaLanguage /> Language
          </TabButton>
          <TabButton 
            $active={activeTab === 'checklist'} 
            onClick={() => setActiveTab('checklist')}
          >
            <FaCheckSquare /> Checklist
          </TabButton>
        <div style={{ 
          marginLeft: 'auto', 
          display: 'flex', 
          alignItems: 'center',
          fontSize: '0.9rem',
          color: colors.muted
        }}>
          <FaMapMarkedAlt style={{ marginRight: '0.5rem' }} />
          Portugal • {formatTime(currentTime)} • 25°C ☀️
        </div>
      </div>

        {activeTab === 'emergency' && (
          <Section>
            <SectionTitle>
              <FaHospital style={{ marginRight: '0.5rem' }} />
              Emergency Contacts
            </SectionTitle>
            
            <h4 style={{ color: colors.primary, margin: '1rem 0 0.5rem' }}>Immediate Assistance</h4>
            <ItemList>
              {emergencyContacts.immediate.map((contact, index) => (
                <Item key={`immediate-${index}`}>
                  <Icon>{contact.icon}</Icon>
                  <div>
                    <div>{contact.name}</div>
                    <a href={`tel:${contact.number.replace(/\s+/g, '')}`} style={{ 
                      color: colors.primary, 
                      textDecoration: 'none',
                      fontWeight: 500,
                      fontSize: '1.1em'
                    }}>
                      {contact.number}
                    </a>
                  </div>
                </Item>
              ))}
            </ItemList>
            
            <h4 style={{ color: colors.primary, margin: '1.5rem 0 0.5rem' }}>Medical</h4>
            <ItemList>
              {emergencyContacts.medical.map((contact, index) => (
                <Item key={`medical-${index}`}>
                  <Icon>{contact.icon}</Icon>
                  <div>
                    <div>{contact.name}</div>
                    <a href={`tel:${contact.number.replace(/\s+/g, '')}`} style={{ 
                      color: colors.primary, 
                      textDecoration: 'none',
                      fontWeight: 500,
                      fontSize: '1.1em'
                    }}>
                      {contact.number}
                    </a>
                  </div>
                </Item>
              ))}
            </ItemList>
            
            <h4 style={{ color: colors.primary, margin: '1.5rem 0 0.5rem' }}>Embassies</h4>
            <ItemList>
              {emergencyContacts.embassies.map((contact, index) => (
                <Item key={`embassy-${index}`}>
                  <Icon>{contact.icon}</Icon>
                  <div>
                    <div>{contact.name}</div>
                    <a href={`tel:${contact.number.replace(/\s+/g, '')}`} style={{ 
                      color: colors.primary, 
                      textDecoration: 'none',
                      fontWeight: 500,
                      fontSize: '1.1em'
                    }}>
                      {contact.number}
                    </a>
                  </div>
                </Item>
              ))}
            </ItemList>
          </Section>
        )}

        {activeTab === 'transport' && (
          <Section>
            <SectionTitle>
              <FaTrain style={{ marginRight: '0.5rem' }} />
              Getting Around Portugal
            </SectionTitle>
            
            <h4 style={{ color: colors.primary, margin: '1rem 0 0.5rem' }}>Public Transport</h4>
            <ItemList>
              {transportInfo.publicTransport.map((item, index) => (
                <Item key={`transport-${index}`}>
                  <Icon>{item.icon}</Icon>
                  <div>
                    <TransportLink href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.name}
                    </TransportLink>
                    <div style={{ fontSize: '0.85rem', color: colors.muted }}>{item.description}</div>
                  </div>
                </Item>
              ))}
            </ItemList>
            
            <h4 style={{ color: colors.primary, margin: '1.5rem 0 0.5rem' }}>Ride Hailing</h4>
            <ItemList>
              {transportInfo.rideHailing.map((item, index) => (
                <Item key={`ride-${index}`}>
                  <Icon>{item.icon}</Icon>
                  <div>
                    <TransportLink href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.name}
                      {item.code && <span style={{
                        fontSize: '0.8em',
                        backgroundColor: colors.primary + '15',
                        color: colors.primary,
                        padding: '2px 6px',
                        borderRadius: '4px',
                        marginLeft: '8px',
                        fontWeight: 'normal'
                      }}>Promo: {item.code}</span>}
                    </TransportLink>
                  </div>
                </Item>
              ))}
            </ItemList>
            
            <h4 style={{ color: colors.primary, margin: '1.5rem 0 0.5rem' }}>Travel Tips</h4>
            <ItemList>
              {transportInfo.tips.map((tip, index) => (
                <Item key={`tip-${index}`} style={{ alignItems: 'flex-start' }}>
                  <Icon>•</Icon>
                  <div>{tip}</div>
                </Item>
              ))}
            </ItemList>
          </Section>
        )}

        {activeTab === 'essentials' && (
          <Section>
            <SectionTitle>
              <FaSuitcase style={{ marginRight: '0.5rem' }} />
              Travel Essentials
            </SectionTitle>
            
            <h4 style={{ color: colors.primary, margin: '1rem 0 0.5rem' }}>Documents</h4>
            <ItemList>
              {travelEssentials.documents.map((item, index) => (
                <Item key={`doc-${index}`}>
                  <Icon>{item.icon}</Icon>
                  <div>{item.text}</div>
                </Item>
              ))}
            </ItemList>
            
            <h4 style={{ color: colors.primary, margin: '1.5rem 0 0.5rem' }}>Electronics</h4>
            <ItemList>
              {travelEssentials.electronics.map((item, index) => (
                <Item key={`elec-${index}`}>
                  <Icon>{item.icon}</Icon>
                  <div>{item.text}</div>
                </Item>
              ))}
            </ItemList>
            
            <h4 style={{ color: colors.primary, margin: '1.5rem 0 0.5rem' }}>Clothing</h4>
            <ItemList>
              {travelEssentials.clothing.map((item, index) => (
                <Item key={`cloth-${index}`}>
                  <Icon>{item.icon}</Icon>
                  <div>{item.text}</div>
                </Item>
              ))}
            </ItemList>
          </Section>
        )}
        
        {activeTab === 'language' && (
          <Section>
            <SectionTitle>
              <FaLanguage style={{ marginRight: '0.5rem' }} />
              Portuguese Phrases
            </SectionTitle>
            
            <h4 style={{ color: colors.primary, margin: '1rem 0 0.5rem' }}>Basic Phrases</h4>
            <ItemList>
              {languageInfo.basicPhrases.map((phrase, index) => (
                <Item key={`basic-${index}`} style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{phrase.pt}</div>
                    <div style={{ color: colors.muted, fontSize: '0.9rem' }}>
                      {phrase.en} • <em>{phrase.pronunciation}</em>
                    </div>
                  </div>
                </Item>
              ))}
            </ItemList>
            
            <h4 style={{ color: colors.primary, margin: '1.5rem 0 0.5rem' }}>Emergency Phrases</h4>
            <ItemList>
              {languageInfo.emergencyPhrases.map((phrase, index) => (
                <Item key={`emergency-${index}`} style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: '#e74c3c' }}>{phrase.pt}</div>
                    <div style={{ color: colors.muted, fontSize: '0.9rem' }}>
                      {phrase.en} • <em>{phrase.pronunciation}</em>
                    </div>
                  </div>
                </Item>
              ))}
            </ItemList>
            
            <h4 style={{ color: colors.primary, margin: '1.5rem 0 0.5rem' }}>Dining Phrases</h4>
            <ItemList>
              {languageInfo.diningPhrases.map((phrase, index) => (
                <Item key={`dining-${index}`} style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{phrase.pt}</div>
                    <div style={{ color: colors.muted, fontSize: '0.9rem' }}>
                      {phrase.en} • <em>{phrase.pronunciation}</em>
                    </div>
                  </div>
                </Item>
              ))}
            </ItemList>
          </Section>
        )}

        {activeTab === 'checklist' && (
          <Section>
            <SectionTitle>
              <FaCheckSquare style={{ marginRight: '0.5rem' }} />
              Portugal Travel Checklist
            </SectionTitle>
            
            <div style={{
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              border: '1px solid #e9ecef'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
                fontSize: '0.9rem'
              }}>
                <span>Preparedness:</span>
                <span style={{ fontWeight: 600 }}>{totalProgress()}% Complete</span>
              </div>
              <div style={{
                height: '8px',
                background: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '0.5rem'
              }}>
                <div style={{
                  width: `${totalProgress()}%`,
                  height: '100%',
                  background: colors.primary,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.8rem',
                color: colors.muted
              }}>
                <span>{Object.values(checklist).flat().filter(item => item.checked).length} of {Object.values(checklist).flat().length} tasks</span>
                <span>Last updated: {new Date().toLocaleDateString()}</span>
              </div>
            </div>

            {Object.entries(currentChecklist).map(([category, items]) => (
              <div key={category} style={{ marginBottom: '1.5rem' }}>
                <h4 style={{
                  color: colors.primary,
                  margin: '0 0 0.75rem',
                  paddingBottom: '0.5rem',
                  borderBottom: `1px solid ${colors.border}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>{category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}</span>
                  <span style={{
                    fontSize: '0.8rem',
                    background: colors.primary + '15',
                    color: colors.primary,
                    padding: '0.2rem 0.5rem',
                    borderRadius: '10px'
                  }}>
                    {getProgress(items)}% Complete
                  </span>
                </h4>
                <ItemList>
                  {items.map(item => (
                    <Item 
                      key={item.id}
                      style={{
                        opacity: item.checked ? 0.7 : 1,
                        textDecoration: item.checked ? 'line-through' : 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleCheckboxToggle(category, item.id)}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        width: '100%'
                      }}>
                        {item.checked ? (
                          <MdCheckBox size={20} color={colors.primary} />
                        ) : (
                          <MdCheckBoxOutlineBlank size={20} color={colors.border} />
                        )}
                        <div style={{
                          flex: 1,
                          color: item.checked ? colors.muted : 'inherit'
                        }}>
                          {item.text}
                        </div>
                      </div>
                    </Item>
                  ))}
                </ItemList>
              </div>
            ))}
            
            <div style={{
              marginTop: '2rem',
              padding: '1rem',
              background: '#f8f9fa',
              borderRadius: '8px',
              borderLeft: `4px solid ${colors.primary}`
            }}>
              <h4 style={{ margin: '0 0 0.5rem', color: colors.primary }}>💡 Tips</h4>
              <ul style={{
                margin: '0',
                paddingLeft: '1.25rem',
                color: colors.muted,
                fontSize: '0.9rem',
                lineHeight: '1.6'
              }}>
                <li>Start checking items off as you pack them before your trip</li>
                <li>Keep important documents in your carry-on luggage</li>
                <li>Make digital copies of important documents and store them securely online</li>
                <li>Check the weather forecast before finalizing your packing</li>
              </ul>
            </div>
          </Section>
        )}
      </Panel>
    </>
  );
};

export default EssentialsPanel;
